import { useEffect, useState } from 'react'
import { Helmet } from 'react-helmet-async'
import { useTranslation } from 'react-i18next'
import { Outlet, generatePath, matchPath, useLocation, useNavigate } from 'react-router-dom'

import { useSession } from '@/modules/access/hooks/useSession'
import useSessionUser from '@/modules/access/hooks/useSessionUser'
import OrganisationHierarchy from '@/modules/admin-center/organisation-hierarchy/OrganisationHierarchy'
import OrganisationSettingsDetails from '@/modules/admin-center/organisation-settings/details/OrganisationSettingsDetails'
import { useGetOrganisationSettingsPageHeader } from '@/modules/admin-center/organisation-settings/hooks/useGetOrganisationSettingsPageHeader'
import { Breadcrumb } from '@/modules/shared/components/breadcrumb/Breadcrumb'
import { Container } from '@/modules/shared/components/container/Container'
import HeaderTabs, { TabItem } from '@/modules/shared/components/header-tabs/HeaderTabs'
import { MoreOptionsMenu } from '@/modules/shared/components/more-options/MoreOptionsMenu'
import { PageHeader } from '@/modules/shared/components/page-header/PageHeader'
import { QueryResult } from '@/modules/shared/components/query-result/QueryResult'
import { HELP_LINK_ADMIN_CENTER_PAGE_HEADER } from '@/modules/shared/help'
import useOrganisationId from '@/modules/shared/hooks/params/useOrganisationId'
import { useViewType } from '@/modules/shared/hooks/useViewType'
import { HomeIcon } from '@/modules/shared/icons/HomeIcon'
import { InfoIcon } from '@/modules/shared/icons/InfoIcon'
import { SettingsIcon } from '@/modules/shared/icons/SettingsIcon'
import { Header } from '@/modules/shared/layouts/header/Header'
import { MoreOptionsItem } from '@/modules/shared/types/MoreOptionsItem'
import { classNames } from '@/modules/shared/utils/classNames'
import { findActiveRoute } from '@/modules/shared/utils/findActiveRoute'

export default function OrganisationSettingsPageHeader() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const { currentOrganisation, userPermissions } = useSessionUser()
  const organisationId = useOrganisationId()
  const { organisation, loadingStates, error } = useGetOrganisationSettingsPageHeader()
  const { id, name, isAPurchaser } = organisation || {}
  const { currentUser } = useSession()
  const { isUtility, isSupplier } = useViewType()
  const [showInfo, setShowInfo] = useState(false)

  const actionList: TabItem[] = [
    { target: 'general', label: t('general.general', 'General') },
    {
      target: 'customise',
      label: t('general.customise', 'Customise'),
      hiddenOnHeaderTabs: !isAPurchaser || !currentUser?.isAdmin,
    },
    {
      target: 'ap-system',
      label: t('general.APSystem', 'AP System'),
      hiddenOnHeaderTabs: !isAPurchaser || !currentUser?.isAdmin,
    },
    {
      target: 'trade-relationships',
      label: t('general.tradeRelationships', 'Trade Relationships'),
      hiddenOnHeaderTabs: !isUtility,
    },
    {
      target: 'accounts',
      label: t('general.accounts', 'Accounts'),
      hiddenOnHeaderTabs: !isAPurchaser || !userPermissions?.PURCHASER_CAN_MANAGE_AP_SYSTEM,
    },
    {
      target: 'invoicing',
      label: t('general.invoicing', 'Invoicing'),
      hiddenOnHeaderTabs: !isAPurchaser,
    },
    {
      target: 'inventory',
      label: t('general.inventory', 'Inventory'),
      hiddenOnHeaderTabs: !isAPurchaser || !currentUser?.isAdmin,
    },
    {
      target: 'delivery-fees',
      label: t('general.deliveryFees', 'Delivery Fees'),
      hiddenOnHeaderTabs: !!isAPurchaser,
    },
    {
      target: 'destinations',
      label: t('general.destinations', 'Destinations'),
      hiddenOnHeaderTabs: !!isAPurchaser,
    },
    {
      target: 'scanning-preferences',
      label: t('general.scanningPreferences', 'Scanning Preferences'),
      hiddenOnHeaderTabs: !!isAPurchaser || !isUtility,
    },
    {
      target: 'public-shop',
      label: t('general.publicShop', 'Public Shop'),
      hiddenOnHeaderTabs: !isUtility || !!isAPurchaser,
    },
    { target: 'advanced', label: t('general.advanced', 'Advanced'), hiddenOnHeaderTabs: !isUtility },
    {
      target: 'delivery-addresses',
      label: t('general.deliveryAddresses', 'Delivery Addresses'),
      hiddenOnHeaderTabs: !isAPurchaser,
    },
    { target: 'audit-log', label: t('general.auditLog', 'Audit Log'), hiddenOnHeaderTabs: true },
  ]

  const organisationActionList: TabItem[] = [
    { target: 'general', label: t('general.general', 'General') },
    {
      target: 'accounts',
      label: t('general.accounts', 'Accounts'),
    },
    {
      target: 'invoicing',
      label: t('general.invoicing', 'Invoicing'),
    },
    {
      target: 'delivery-addresses',
      label: t('general.deliveryAddresses', 'Delivery Addresses'),
    },
  ]

  const isRedirect = matchPath('/settings/organisations/:organisationId', location.pathname)

  useEffect(() => {
    if (isRedirect) {
      navigate(
        generatePath(`/${isUtility ? 'utilities' : 'settings'}/organisations/:organisationId/general`, {
          organisationId: String(id),
        })
      )
    } else if (organisationId === String(currentOrganisation?.id) && !isUtility) {
      // Always open organisation settings as current organisation
      navigate(
        generatePath('/settings/organisations/:organisationId/general', {
          organisationId: String(currentOrganisation?.id),
        })
      )
    }
  }, [])

  return (
    <>
      <Helmet>
        <title>{`${t('general.organisationSettings', 'Organisation Settings')} ${name}`}</title>
      </Helmet>
      {isUtility && <Header />}
      <QueryResult loading={loadingStates.loading} error={error}>
        <Container layout="md" className={classNames({ 'mt-5 pt-[4.3125rem]': isUtility })}>
          <Breadcrumb className="rounded-md bg-gray-200 px-3 py-2" data-testid="invoice-breadcrumb">
            <Breadcrumb.Item icon={HomeIcon} href="/dashboard" />
            {isUtility ? (
              <>
                <Breadcrumb.Item href="/utilities">{t('general.utilities', 'Utilities')}</Breadcrumb.Item>
                <Breadcrumb.Item href="/utilities/organisations">
                  {t('general.organisations', 'Organisations')}
                </Breadcrumb.Item>
                {isAPurchaser ? (
                  <Breadcrumb.Item href="/utilities/organisations/purchasers">
                    {t('general.purchasers', 'Purchasers')}
                  </Breadcrumb.Item>
                ) : (
                  <Breadcrumb.Item href="/utilities/organisations/suppliers">
                    {t('general.suppliers', 'Suppliers')}
                  </Breadcrumb.Item>
                )}
              </>
            ) : (
              <Breadcrumb.Item href={isSupplier ? '#' : '/organisations'}>
                {t('general.organisations', 'Organisations')}
              </Breadcrumb.Item>
            )}
            <Breadcrumb.Item href="#">{name}</Breadcrumb.Item>
            <Breadcrumb.Item>{findActiveRoute(actionList, location.pathname)}</Breadcrumb.Item>
          </Breadcrumb>
          <section className="my-5 flex flex-col gap-y-2">
            <PageHeader
              title={isUtility ? name || '' : t('general.organisationSettings', 'Organisation Settings')}
              subTitle={
                isUtility
                  ? isAPurchaser
                    ? t('general.purchaserOrganisation', 'Purchaser Organisation')
                    : t('general.supplierOrganisation', 'Supplier Organisation')
                  : name || ''
              }
              icon={SettingsIcon}
              testId="admin-header"
              actions={[
                {
                  tooltip: t('general.showOrHideDetails', 'Show or Hide Details'),
                  testId: 'show-details-button',
                  onClick: () => setShowInfo(!showInfo),
                  icon: InfoIcon,
                  show: isUtility,
                },
                {
                  title: t('general.moreOptions', 'More Options'),
                  render: () => (
                    <MoreOptionsMenu
                      components={[MoreOptionsItem.OpenInPPlus, MoreOptionsItem.Help, MoreOptionsItem.AuditLog]}
                      buttonTheme="dark"
                      iconClass="size-8"
                      helpLink={HELP_LINK_ADMIN_CENTER_PAGE_HEADER}
                    />
                  ),
                },
              ]}
            />
            {showInfo && isUtility && <OrganisationSettingsDetails />}
            <OrganisationHierarchy organisationId={Number(organisationId)} isHeader />
            {!isUtility && (
              <section className="flex items-center justify-between rounded-md bg-white px-4 py-3 text-sm shadow">
                <p>{t('general.currentlySelectedOrganisation', 'Currently Selected Organisation')}</p>
                <div className="rounded-md bg-primary/10 px-3 py-1.5 text-xs text-primary">{name}</div>
              </section>
            )}
            <HeaderTabs
              actionList={
                isUtility || organisationId === String(currentOrganisation?.id) ? actionList : organisationActionList
              }
            />
          </section>
          <Outlet />
          {isUtility && <div className="h-96" />}
        </Container>
      </QueryResult>
    </>
  )
}
